import { BedDouble, Check, Maximize2, Users } from "lucide-react";
import { money } from "../lib/api";
import { RoomGallery } from "./RoomGallery";
import { AmenityList } from "./Amenities";

const RatePlan = ({ plan, currency, selected, onSelect, roomId }) => (
  <div className={`flex flex-col gap-4 rounded-xl border p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${selected ? "border-brand bg-brand-light" : "border-ink/10 hover:border-brand/30"}`}
    data-testid={`rate-${roomId}-${plan.id}`}>
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-ink">{plan.name}</p>
        {plan.special && <span className="badge bg-gold/20 text-ink">Special</span>}
        {plan.package && <span className="badge bg-brand text-sand">Package</span>}
        {!plan.refundable && <span className="badge bg-ink/5 text-ink/60">Non-refundable</span>}
      </div>
      <ul className="mt-2 space-y-1">
        {plan.inclusions.map((x) => <li key={x} className="flex items-center gap-2 text-xs text-ink/60"><Check className="h-3 w-3 text-brand" />{x}</li>)}
      </ul>
    </div>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 sm:shrink-0 sm:flex-col sm:items-end sm:gap-1">
      {plan.original_total > plan.total && <span className="font-mono text-xs text-ink/40 line-through">{money(plan.original_total, currency)}</span>}
      <span className="font-mono text-lg text-ink" data-testid={`rate-total-${roomId}-${plan.id}`}>{money(plan.total, currency)}</span>
      <span className="text-xs text-ink/50">avg {money(plan.avg_nightly, currency)} / night</span>
      <button onClick={onSelect} className={`${selected ? "btn-primary !py-2" : "btn-outline !py-2"} ml-auto sm:ml-0`} data-testid={`select-rate-${roomId}-${plan.id}`}>
        {selected ? "Selected" : "Select"}
      </button>
    </div>
  </div>
);

export const RoomCard = ({ room, currency, selectedRateId, onSelect }) => (
  <article className={`overflow-hidden rounded-2xl border border-brand/10 bg-white ${room.available ? "" : "opacity-60"}`} data-testid={`room-card-${room.id}`}>
    <div className="grid grid-cols-[minmax(0,1fr)] md:grid-cols-[260px_minmax(0,1fr)]">
      <RoomGallery room={room} className="h-56 w-full object-cover md:h-full md:min-h-[260px]" />
      <div className="min-w-0 p-5 sm:p-6">
        <h3 className="break-words font-serif text-3xl text-ink">{room.name}</h3>
        <p className="mt-1 text-sm text-ink/60">{room.description}</p>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-ink/60">
          {room.size && <span className="flex items-center gap-1.5"><Maximize2 className="h-3.5 w-3.5 text-brand" />{room.size} m²</span>}
          <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-brand" />Up to {room.max_adults} adults, {room.max_kids} kids</span>
          <span className="flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5 text-brand" />King or twin</span>
        </div>
        {room.features?.length > 0 && <div className="mt-3"><AmenityList features={room.features} testid={`room-features-${room.id}`} /></div>}
        <div className="mt-5 space-y-3">
          {room.available ? (
            room.rate_plans.length ? room.rate_plans.map((p) => (
              <RatePlan key={p.id} plan={p} currency={currency} roomId={room.id} selected={selectedRateId === p.id} onSelect={() => onSelect(p)} />
            )) : <p className="text-sm text-ink/50">No rate plans match your package filter.</p>
          ) : (
            <p className="rounded-xl bg-linen p-4 text-sm text-ink/60" data-testid={`room-unavailable-${room.id}`}>{room.reason}</p>
          )}
        </div>
      </div>
    </div>
  </article>
);
